import type { JSX } from 'react';
import { DateTime } from '@ksyos/date-time';
import { Radio } from '@ksyos/design-system';
import { useEffect } from 'react';
import type { Process } from '@common/interfaces';
import type { BariPostNurseSpecialistFollowUpDataV2 } from '@common/validation/customControls/bariPost/bariPostNurseSpecialistFollowUp/v2/bariPostNurseSpecialistFollowUpDataV2';
import { AdjuvanteGewichtsreducerendeMedicatieOpties } from '@common/customControls/bariatrics';
import { CustomControlCode, ProcessActivityStatus, ValidationSchemaVersion } from '@common/enums';
import { getValidatedCustomControlData } from '@common/workflow/serviceActivityRegistration';
import { ProcessContext } from '@frontend/components/pages/episodeCategory/carePath/process/processContext';
import { useProcessActivityEditContext } from '@frontend/utils';
import { useDefinedContext } from '@frontend/utils/hooks/useDefinedContext';
import {
    BooleanRadioGroupFormGroup,
    DateInputFormGroup,
    RadioGroupFormGroup,
} from '../../../processActivityFormGroupFields';
import { followUpStatusOpties } from '../BariPostNurseSpecialistFollowUpComorbiditeitenStatusEdit';

const fieldKeys = ['depressie', 'longziekte'];

const adjuvanteGewichtsreducerendeMedicatieOpties = [
    AdjuvanteGewichtsreducerendeMedicatieOpties.orlistat,
    AdjuvanteGewichtsreducerendeMedicatieOpties.naltrexon_bupropion,
    AdjuvanteGewichtsreducerendeMedicatieOpties.liraglutide,
    AdjuvanteGewichtsreducerendeMedicatieOpties.semaglutide,
    AdjuvanteGewichtsreducerendeMedicatieOpties.setmelanotide,
    AdjuvanteGewichtsreducerendeMedicatieOpties.anders,
];

export function BariPostNurseSpecialistFollowUpComorbiditeitenAdditionalFieldsV2(): JSX.Element {
    const { data, onChange, processActivityId } =
        useProcessActivityEditContext<BariPostNurseSpecialistFollowUpDataV2>();
    const { process } = useDefinedContext(ProcessContext);

    useEffect(() => {
        if (!processActivityId) {
            loadNurseSpecialistFollowUpCustomControlData();
        }
    }, [processActivityId, process]);

    return (
        <>
            {fieldKeys.map((fieldKey) => (
                <RadioGroupFormGroup
                    fieldKey={`${fieldKey}_status`}
                    isRequired={true}
                    key={fieldKey}
                    label={`Status ${fieldKey}`}
                >
                    {followUpStatusOpties.map((value) => (
                        <Radio
                            key={value}
                            label={value}
                            value={value}
                        />
                    ))}
                </RadioGroupFormGroup>
            ))}
            <BooleanRadioGroupFormGroup
                fieldKey="start_adjuvante_gewichtsreducerende_medicatie"
                isRequired={true}
                label="Start adjuvante gewichtsreducerende medicatie"
                onChange={handleChangeVeranderingen}
                type="radio"
            />
            {data.start_adjuvante_gewichtsreducerende_medicatie && (
                <>
                    <RadioGroupFormGroup
                        fieldKey="adjuvante_gewichtsreducerende_medicatie"
                        isClearable={true}
                        label="Medicatie"
                    >
                        {adjuvanteGewichtsreducerendeMedicatieOpties.map((value) => (
                            <Radio
                                key={value}
                                label={value}
                                value={value}
                            />
                        ))}
                    </RadioGroupFormGroup>
                    {data.adjuvante_gewichtsreducerende_medicatie && (
                        <>
                            <DateInputFormGroup
                                fieldKey="adjuvante_gewichtsreducerende_medicatie_startdatum"
                                label="Startdatum medicatie"
                            />
                            <DateInputFormGroup
                                fieldKey="adjuvante_gewichtsreducerende_medicatie_stopdatum"
                                label="Stopdatum medicatie"
                            />
                        </>
                    )}
                </>
            )}
        </>
    );

    async function handleChangeVeranderingen(value: boolean | null): Promise<void> {
        return onChange(
            {
                adjuvante_gewichtsreducerende_medicatie: null,
                adjuvante_gewichtsreducerende_medicatie_startdatum: null,
                adjuvante_gewichtsreducerende_medicatie_stopdatum: null,
                start_adjuvante_gewichtsreducerende_medicatie: value,
            },
            'start_adjuvante_gewichtsreducerende_medicatie'
        );
    }

    function loadNurseSpecialistFollowUpCustomControlData(): void {
        const latestNurseSpecialistFollowUpData = getLatestNurseSpecialistFollowUpData(process);

        if (
            !latestNurseSpecialistFollowUpData ||
            Boolean(latestNurseSpecialistFollowUpData.adjuvante_gewichtsreducerende_medicatie_stopdatum)
        ) {
            return;
        }

        onChange({
            adjuvante_gewichtsreducerende_medicatie:
                latestNurseSpecialistFollowUpData.adjuvante_gewichtsreducerende_medicatie,
            adjuvante_gewichtsreducerende_medicatie_startdatum:
                latestNurseSpecialistFollowUpData.adjuvante_gewichtsreducerende_medicatie_startdatum,
            start_adjuvante_gewichtsreducerende_medicatie:
                latestNurseSpecialistFollowUpData.start_adjuvante_gewichtsreducerende_medicatie,
        });
    }
}

function getLatestNurseSpecialistFollowUpData(process: Process): BariPostNurseSpecialistFollowUpDataV2 | undefined {
    const followUpActivities = process.activities.filter(
        (activity) =>
            activity.status === ProcessActivityStatus.complete &&
            activity.serviceActivity.customControlCode === CustomControlCode.BariPostNurseSpecialistFollowUp &&
            activity.customControlVersion === 2
    );
    if (!followUpActivities.length) {
        return undefined;
    }

    const latestActivity = followUpActivities.reduce((previousLatest, current) => {
        const previousLatestPerformedDateTime = DateTime.from(previousLatest.performedDatetime);
        const currentPerformedDateTime = DateTime.from(current.performedDatetime);

        return currentPerformedDateTime.isAfter(previousLatestPerformedDateTime) ? current : previousLatest;
    });

    return getValidatedCustomControlData<BariPostNurseSpecialistFollowUpDataV2>(
        CustomControlCode.BariPostNurseSpecialistFollowUp,
        latestActivity.customControlData,
        ValidationSchemaVersion.final,
        latestActivity.customControlVersion
    );
}
